import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Trash2, Search, X, UserPlus, Loader2, Eye, EyeOff, Building2 } from "lucide-react";

const ROLES = ["admin", "teacher", "student"];

const roleTone = {
  admin: "bg-indigo-50 text-indigo-700",
  teacher: "bg-amber-50 text-amber-700",
  student: "bg-emerald-50 text-emerald-700",
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({}); // user_id -> bool
  const [bulkRole, setBulkRole] = useState("student");
  const [err, setErr] = useState("");
  const [banner, setBanner] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addErr, setAddErr] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student", institution_id: "" });

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
  }, []);

  const changeRole = async (id, role) => {
    await api.patch(`/admin/users/${id}/role`, { role });
    load();
  };
  const removeUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/admin/users/${id}`);
    load();
  };

  const filtered = users.filter(
    (u) => u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase())
  );
  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
  const selectedCount = selectedIds.length;
  const allChecked = filtered.length > 0 && filtered.every((u) => selected[u.id]);
  const someChecked = selectedCount > 0 && !allChecked;

  const toggleAll = () => {
    if (allChecked) {
      const next = { ...selected };
      filtered.forEach((u) => delete next[u.id]);
      setSelected(next);
    } else {
      const next = { ...selected };
      filtered.forEach((u) => {
        if (u.id !== me?.id) next[u.id] = true;
      });
      setSelected(next);
    }
  };

  const clearSelection = () => setSelected({});

  const bulkRoleChange = async () => {
    setErr("");
    setBanner("");
    try {
      const { data } = await api.post("/admin/users/bulk-role", {
        user_ids: selectedIds,
        role: bulkRole,
      });
      setBanner(`Updated ${data.modified} user${data.modified === 1 ? "" : "s"} to '${bulkRole}'.`);
      clearSelection();
      load();
    } catch (ex) {
      setErr(formatApiError(ex));
    }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedCount} selected users? This cannot be undone.`)) return;
    setErr("");
    setBanner("");
    try {
      const { data } = await api.post("/admin/users/bulk-delete", {
        user_ids: selectedIds,
      });
      setBanner(`Deleted ${data.deleted} user${data.deleted === 1 ? "" : "s"}.`);
      clearSelection();
      load();
    } catch (ex) {
      setErr(formatApiError(ex));
    }
  };

  const openAdd = () => {
    setAddErr("");
    setShowPw(false);
    setForm({ name: "", email: "", password: "", role: "student", institution_id: "" });
    setAddOpen(true);
    api.get("/admin/institutions")
      .then((r) => setInstitutions(r.data || []))
      .catch(() => setInstitutions([]));
  };
  const closeAdd = () => {
    if (addBusy) return;
    setAddOpen(false);
  };
  const submitAdd = async (e) => {
    e.preventDefault();
    setAddErr("");
    setBanner("");
    setErr("");
    setAddBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
      };
      if (form.institution_id) payload.institution_id = form.institution_id;
      const { data } = await api.post("/admin/users", payload);
      const emailMsg = data.welcome_email_sent
        ? " A welcome email with the temporary password was sent."
        : " (Welcome email not delivered — check the RESEND_API_KEY or backend logs.)";
      setBanner(`Created ${data.role} account for ${data.email}.${emailMsg}`);
      setAddOpen(false);
      load();
    } catch (ex) {
      setAddErr(formatApiError(ex));
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <AppShell title="Users" subtitle="Manage roles and access across the platform.">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
            data-testid="user-search-input"
          />
        </div>
        <button
          onClick={openAdd}
          className="btn-primary text-sm"
          data-testid="add-user-button"
        >
          <UserPlus className="w-4 h-4" /> Add user
        </button>
      </div>

      {banner && (
        <div className="mb-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2" data-testid="users-banner">
          {banner}
        </div>
      )}
      {err && (
        <div className="mb-3 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-2" data-testid="users-error">
          {err}
        </div>
      )}

      {selectedCount > 0 && (
        <div
          className="sticky top-16 z-20 mb-4 rounded-2xl border border-indigo-200 bg-white shadow-lg p-4 flex flex-wrap items-center gap-3"
          data-testid="bulk-toolbar"
        >
          <span className="text-sm font-semibold text-slate-800" data-testid="bulk-count">
            {selectedCount} selected
          </span>
          <span className="text-slate-300">|</span>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Change role to</label>
            <select
              value={bulkRole}
              onChange={(e) => setBulkRole(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg text-sm px-2 py-1 focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="bulk-role-select"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <button onClick={bulkRoleChange} className="btn-primary text-xs" data-testid="bulk-role-apply">
              Apply
            </button>
          </div>
          <span className="text-slate-300">|</span>
          <button onClick={bulkDelete} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700 transition" data-testid="bulk-delete">
            <Trash2 className="w-3.5 h-3.5" /> Delete selected
          </button>
          <button onClick={clearSelection} className="ml-auto text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 text-xs" data-testid="bulk-clear">
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        </div>
      )}

      <div className="qs-card overflow-hidden" data-testid="users-table">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
              <tr>
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked; }}
                    onChange={toggleAll}
                    data-testid="bulk-select-all"
                  />
                </th>
                <th className="text-left px-4 py-3 font-bold">Name</th>
                <th className="text-left px-4 py-3 font-bold">Email</th>
                <th className="text-left px-4 py-3 font-bold">Role</th>
                <th className="text-left px-4 py-3 font-bold">Joined</th>
                <th className="text-right px-4 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={!!selected[u.id]}
                      disabled={u.id === me?.id}
                      onChange={(e) => setSelected({ ...selected, [u.id]: e.target.checked })}
                      title={u.id === me?.id ? "You can't select yourself" : ""}
                      data-testid={`user-select-${u.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-800">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-600 text-white grid place-items-center text-xs font-bold">
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      {u.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${roleTone[u.role]}`}>{u.role}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u.id, e.target.value)}
                        className="bg-white border border-slate-200 rounded-lg text-sm px-2 py-1 focus:ring-2 focus:ring-indigo-500 outline-none"
                        data-testid={`role-select-${u.id}`}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => removeUser(u.id)}
                        disabled={u.id === me?.id}
                        className="text-rose-500 disabled:opacity-30"
                        title={u.id === me?.id ? "Cannot delete yourself" : "Delete user"}
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-500" data-testid="no-users">No users found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {addOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4"
          onClick={closeAdd}
          data-testid="add-user-modal"
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h3 className="font-display text-lg font-bold text-slate-900">Add a new user</h3>
                <p className="text-xs text-slate-500 mt-0.5">The account is pre-verified and ready to use.</p>
              </div>
              <button
                onClick={closeAdd}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Close"
                data-testid="add-user-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submitAdd} className="px-6 py-5 space-y-4" data-testid="add-user-form">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Full name</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition"
                  placeholder="Jamie Rivera"
                  data-testid="add-user-name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Email</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition"
                  placeholder="new.user@prismiq.com"
                  data-testid="add-user-email"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Temporary password</label>
                <div className="relative mt-1.5">
                  <input
                    required
                    type={showPw ? "text" : "password"}
                    minLength={6}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 pr-11 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition font-mono text-sm"
                    placeholder="At least 6 characters"
                    data-testid="add-user-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-800"
                    aria-label={showPw ? "Hide password" : "Show password"}
                    data-testid="add-user-password-toggle"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">Share this with the user — they can change it after signing in.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Role</label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  {ROLES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setForm({ ...form, role: r })}
                      className={`px-3 py-2 rounded-xl border text-sm font-semibold capitalize transition ${
                        form.role === r
                          ? "bg-indigo-600 border-indigo-600 text-white"
                          : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                      }`}
                      data-testid={`add-user-role-${r}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600 flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" /> Institution <span className="text-slate-400 font-medium tracking-normal normal-case">(optional)</span>
                </label>
                <select
                  value={form.institution_id}
                  onChange={(e) => setForm({ ...form, institution_id: e.target.value })}
                  className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition text-sm"
                  data-testid="add-user-institution"
                >
                  <option value="">— No institution —</option>
                  {institutions.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name} · {i.code}
                    </option>
                  ))}
                </select>
              </div>
              {addErr && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="add-user-error">
                  {addErr}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeAdd}
                  disabled={addBusy}
                  className="btn-ghost text-sm disabled:opacity-50"
                  data-testid="add-user-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addBusy}
                  className="btn-primary text-sm disabled:opacity-70"
                  data-testid="add-user-submit"
                >
                  {addBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  {addBusy ? "Creating…" : "Create user"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
}
